import { defineStore } from 'pinia'
import type { ISampleArea } from '@team-milva/lab-models/resources'
import { SampleArea } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'

type CreateSampleAreaParams = {
  name: string
  icon?: string
  labReportTemplateID: string
}

type UpdateSampleAreaParams = {
  name?: string
  icon?: string
  labReportTemplateID?: string
  activeState?: 'active' | 'inactive'
}

export const useSampleAreaStore = defineStore('sampleArea', {
  state: () => ({
    allSampleAreas: [] as SampleArea[],
    unsubscribeAllSampleAreas: null as (() => void) | null,
  }),

  getters: {
    allSampleAreasLoaded(): boolean {
      return !!this.unsubscribeAllSampleAreas
    },
  },

  actions: {
    getSampleAreas(): Promise<SampleArea[]> {
      if (this.allSampleAreasLoaded) {
        return Promise.resolve(this.allSampleAreas)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'SampleAreas'),
          where('accountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allSampleAreas = querySnapshot.docs
            .map((doc) => new SampleArea(doc.data() as ISampleArea))
            .filter((x) => x.activeState !== 'deleted')
          this.unsubscribeAllSampleAreas = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allSampleAreas)
          }
        })
      })
    },

    async createSampleArea(params: CreateSampleAreaParams): Promise<string> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/sample-areas',
        {
          path: { labAccountID },
          body: {
            name: params.name,
            icon: params.icon,
            labReportTemplateID: params.labReportTemplateID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data.id as string
    },

    async updateSampleArea(
      sampleAreaID: string,
      params: UpdateSampleAreaParams
    ): Promise<{ followCount: number; skippedCount: number }> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/sample-areas/{sampleAreaID}',
        {
          path: { labAccountID, sampleAreaID },
          body: params,
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data as { followCount: number; skippedCount: number }
    },

    async deactivateSampleArea(sampleAreaID: string): Promise<void> {
      await this.updateSampleArea(sampleAreaID, { activeState: 'inactive' })
    },

    async reactivateSampleArea(sampleAreaID: string): Promise<void> {
      await this.updateSampleArea(sampleAreaID, { activeState: 'active' })
    },

    // Hydrates from a Display's public data endpoint — see orderStore.ts.
    hydrateFromPublicData(raw: unknown[]): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.allSampleAreas = (raw as any[])
        .map((x) => new SampleArea(x))
        .filter((x) => x.activeState !== 'deleted')
    },

    resetStore(): void {
      this.unsubscribeAllSampleAreas?.()
      this.allSampleAreas = []
      this.unsubscribeAllSampleAreas = null
    },
  },
})
